import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { BOOKS } from "../data/books";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { value: "facebook", label: "Facebook / Instagram post" },
  { value: "x", label: "X (Twitter) post" },
  { value: "amazon", label: "Amazon editorial blurb" },
  { value: "email", label: "Newsletter email" },
];

const selectCls =
  "rounded-none border-0 border-b border-white/20 bg-transparent px-0 font-sans text-sm text-[#F4F4F0] focus:ring-0 focus:border-[#E63946]";

export const AdStudio = () => {
  const [book, setBook] = useState("all");
  const [format, setFormat] = useState("facebook");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const outputRef = useRef(null);

  const generate = async () => {
    if (loading) return;
    setLoading(true);
    setOutput("");
    setCopied(false);
    try {
      const res = await fetch(`${API}/ads/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ book, format }),
      });
      if (!res.ok || !res.body) throw new Error("Request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.text) setOutput((prev) => prev + parsed.text);
          } catch (e) {
            if (e.message !== "Generation failed. Please try again.") continue;
            throw e;
          }
        }
      }
    } catch (e) {
      toast.error("Could not generate ad copy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!output) return;
    await navigator.clipboard.writeText(output);
    setCopied(true);
    toast.success("Ad copy copied to clipboard.");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="ad-studio" data-testid="ad-studio-section" className="relative overflow-hidden py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 55% 45% at 20% 40%, rgba(230,57,70,0.08), transparent 65%)" }}
      />
      <div className="relative z-10 mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-[1fr_1.2fr]">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-[#E63946]" />
            <p className="font-sans text-[10px] font-bold uppercase tracking-[0.4em] text-[#A8A29E]">
              The Ad Studio
            </p>
          </div>
          <h2 className="font-serif text-4xl font-bold leading-[1.02] tracking-tight md:text-6xl">
            Your campaign,
            <br />
            <span className="italic font-medium text-[#A8A29E]">written live.</span>
          </h2>
          <p className="mt-6 max-w-md font-sans text-base font-light leading-relaxed text-[#A8A29E] md:text-lg">
            Pick a book and a format. Our AI copywriter drafts ready-to-post
            advertising for Samkhya Publishing — watch it write, then copy it
            straight into your channels.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="border border-white/10 bg-[#0c0a09]/60 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.5)] backdrop-blur-2xl md:p-10"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <div>
              <label className="mb-2 block font-sans text-[10px] font-bold uppercase tracking-[0.3em] text-[#A8A29E]">
                Book
              </label>
              <Select value={book} onValueChange={setBook}>
                <SelectTrigger data-testid="ad-book-select" className={selectCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-white/10 bg-[#141210] text-[#F4F4F0]">
                  <SelectItem value="all">Full catalogue (all three)</SelectItem>
                  {BOOKS.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-2 block font-sans text-[10px] font-bold uppercase tracking-[0.3em] text-[#A8A29E]">
                Format
              </label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="ad-format-select" className={selectCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-white/10 bg-[#141210] text-[#F4F4F0]">
                  {FORMATS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <button
            onClick={generate}
            disabled={loading}
            data-testid="ad-generate-button"
            className="group mt-10 flex w-full items-center justify-center gap-3 bg-[#E63946] px-6 py-4 font-sans text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#F4F4F0] hover:text-[#0C0A09] disabled:cursor-wait disabled:opacity-70"
          >
            {loading ? (
              <>
                <span className="pulse-dot h-2 w-2 rounded-full bg-current" />
                Writing your ad…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate ad copy
              </>
            )}
          </button>

          <div
            ref={outputRef}
            data-testid="ad-output"
            className="mt-8 min-h-[10rem] border border-white/10 bg-black/40 p-6"
          >
            {output ? (
              <p className="whitespace-pre-wrap font-sans text-sm font-light leading-relaxed text-[#F4F4F0]">
                {output}
                {loading && <span className="blinking-cursor ml-0.5 inline-block h-4 w-[2px] bg-[#E63946] align-middle" />}
              </p>
            ) : (
              <p className="font-sans text-sm font-light italic text-[#57534E]">
                {loading ? "Thinking…" : "Your generated ad copy will appear here."}
              </p>
            )}
          </div>

          {output && !loading && (
            <button
              onClick={copy}
              data-testid="ad-copy-button"
              className="mt-4 flex items-center gap-2 font-sans text-[10px] font-bold uppercase tracking-[0.3em] text-[#A8A29E] transition-colors duration-300 hover:text-[#F4F4F0]"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-[#E63946]" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy to clipboard"}
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};
